import * as RadixDialog from '@radix-ui/react-dialog'
import { X } from 'lucide-react'
import { cn } from '@/utils/cn'
import { Button } from './Button'

type DialogSize = 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full'

interface DialogProps {
  open: boolean
  onClose: () => void
  title?: string
  description?: string
  size?: DialogSize
  children: React.ReactNode
  footer?: React.ReactNode
}

const sizeMap: Record<DialogSize, string> = {
  sm:   'sm:max-w-md',
  md:   'sm:max-w-lg',
  lg:   'sm:max-w-2xl',
  xl:   'sm:max-w-4xl',
  '2xl':'sm:max-w-[85vw]',
  full: 'sm:max-w-[90vw]',
}

export function Dialog({ open, onClose, title, description, size = 'md', children, footer }: DialogProps) {
  return (
    <RadixDialog.Root open={open} onOpenChange={(o) => !o && onClose()}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 animate-fade-in" />
        <RadixDialog.Content
          onCloseAutoFocus={(e) => e.preventDefault()}
          className={cn(
            // Mobile: bottom sheet
            'fixed bottom-0 inset-x-0 w-full flex flex-col',
            'bg-bg-surface border-t border-x border-bg-border rounded-t-2xl',
            'max-h-[88vh] animate-sheet-up',
            // sm+: centered dialog (override mobile positioning)
            'sm:inset-x-auto sm:bottom-auto sm:top-1/2 sm:left-1/2',
            'sm:-translate-x-1/2 sm:-translate-y-1/2',
            'sm:border sm:rounded-xl sm:animate-slide-in-up',
            'sm:max-h-[90vh]',
            sizeMap[size],
            'z-50 focus:outline-none shadow-modal',
          )}
        >
          {/* Drag handle — mobile only */}
          <div className="shrink-0 flex justify-center pt-3 pb-1 sm:hidden">
            <div className="w-12 h-1.5 bg-bg-border rounded-full" />
          </div>

          {/* Header */}
          {title && (
            <div className="shrink-0 flex items-center justify-between px-5 py-3.5 border-b border-bg-border sm:px-6 sm:py-4">
              <div>
                <RadixDialog.Title className="text-base font-semibold text-text-primary sm:text-lg">
                  {title}
                </RadixDialog.Title>
                {description && (
                  <RadixDialog.Description className="text-sm text-text-muted mt-0.5">
                    {description}
                  </RadixDialog.Description>
                )}
              </div>
              <Button variant="ghost" size="sm" onClick={onClose} iconOnly className="shrink-0 ml-2">
                <X className="w-4 h-4" />
              </Button>
            </div>
          )}

          {/* Body — scrollable */}
          <div className="flex-1 overflow-y-auto px-5 py-4 sm:px-6">
            {children}
          </div>

          {/* Footer */}
          {footer && (
            <div className="shrink-0 px-5 py-4 border-t border-bg-border flex items-center justify-end gap-2 sm:px-6">
              {footer}
            </div>
          )}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  )
}

// Confirm Dialog
interface ConfirmDialogProps {
  open: boolean
  onClose: () => void
  onConfirm: () => void
  title: string
  description?: string
  confirmLabel?: string
  variant?: 'danger' | 'primary'
  loading?: boolean
}

export function ConfirmDialog({
  open, onClose, onConfirm,
  title, description,
  confirmLabel = 'Onayla',
  variant = 'danger',
  loading,
}: ConfirmDialogProps) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      description={description}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>İptal</Button>
          <Button variant={variant} loading={loading} onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      {description && <p className="text-text-secondary text-sm">{description}</p>}
    </Dialog>
  )
}
